import {
  type Account,
  type InventoryResource,
  type ResourceType,
  type Snapshot,
  Provider,
} from '@repo/api-gw-sdk';

import useBackupVaults from '@/data/vaults/useBackupVaults';

import type { RestoreGenericStorageProps } from './bucketSelection';
import { supportedCrossCloudRestores } from './restoreFileWizard';
import { S3Configuration } from './steps/s3Configuration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreS3State extends RestoreGenericStorageProps {
  resourceType: ResourceType;
  resourceId: string;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  restoreAccount?: Account;
}

const restoreS3Flow: Step<RestoreS3State> = {
  name: 'SelectTargetAccountStep',
  Component: SelectTargetAccountStep,
  next: [
    {
      name: 'S3Configuration',
      Component: S3Configuration,
    },
  ],
};

export const RestoreS3Wizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const { vaults } = useBackupVaults();

  const cloudProvider = vaults.find((v) => v.id === snapshot.vaultId)
    ?.vaultAttributes.cloudProvider;

  return (
    <Wizard<RestoreS3State>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreS3Flow]}
      initialState={{
        snapshot,
        resourceId: resource.id,
        resourceType: resource.resourceType,
        S3: {},
        GCS: {},
        StorageAccount: {},
        supportedCloudProviders:
          (cloudProvider && supportedCrossCloudRestores[cloudProvider]) ?? [],
      }}
      title={
        resource.cloudProvider === Provider.Aws
          ? 'Restore S3'
          : resource.cloudProvider === Provider.Azure
            ? 'Restore Storage Account'
            : resource.cloudProvider === Provider.Gcp
              ? 'Restore GCS'
              : ''
      }
      stepperLabels={['Restore Account', 'Configuration']}
    />
  );
};
